import {
  readViewerErrorMessages,
  useDeleteAdminInvoiceReportViewersService,
  type OfficeMember,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportViewers.service';
import { useTranslation } from '@/i18n';
import { ButtonV2, useToastAPI } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback } from 'react';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

type ViewerListProps = {
  reportId: string;
  viewers: OfficeMember[];
};

export const ViewerList: FC<ViewerListProps> = memo(({ reportId, viewers }) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { mutateAsync, isPending } =
    useDeleteAdminInvoiceReportViewersService(reportId);

  const onRemove = useCallback(
    async (officeMemberId: string) => {
      try {
        await mutateAsync({ office_member_ids: [officeMemberId] });
        notify.success({
          title: t('invoice_report_share.removed'),
          duration: 3,
        });
      } catch (error) {
        const messages = readViewerErrorMessages(error);
        notify.error({
          title: Array.isArray(messages)
            ? messages.join(',')
            : messages || t('system_error'),
          duration: 3,
        });
      }
    },
    [mutateAsync, notify, t]
  );

  return (
    <div className={cx('container')}>
      <div className={cx('label')}>{t('invoice_report_share.viewers')}</div>
      {viewers.length === 0 ? (
        <div className={cx('empty')}>
          {t('invoice_report_share.no_viewers')}
        </div>
      ) : (
        <ul className={cx('list')}>
          {viewers.map((viewer) => (
            <li key={viewer.id} className={cx('item')}>
              <div className={cx('member')}>
                <span className={cx('name')}>{viewer.name}</span>
                <span className={cx('number')}>{viewer.number}</span>
              </div>
              {viewer.id && (
                <ButtonV2
                  size='sm'
                  isTransparent
                  isDisabled={isPending}
                  onClick={() => onRemove(viewer.id ?? '')}
                  testId={`admin-invoice-report-share-remove-button-${viewer.id}`}
                >
                  {t('invoice_report_share.remove_viewer')}
                </ButtonV2>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
ViewerList.displayName = 'ViewerList';
